import type { ProfileModuleDto } from '@up2stay/up2promo-api-client';
import {
  Card,
  CardContent,
  ResponsiveDialog,
  ResponsiveDialogContent,
  cn,
} from '@up2stay/up2promo-ui';
import type { ComponentProps } from 'react';

import {
  PROFILE_PREVIEW_RAIL_WIDTH_CLASS,
  stickyPhonePreviewRailClass,
} from '@/core/components/layout';
import type { ProfileEditPreviewData } from '@/core/components/profile/edit/use-profile-edit';
import { ProfileModulesSection } from '@/core/components/profile/profile-modules-section';
import { IPhonePreview } from '@/core/components/ui/iphone-preview';
import { triggerTapHaptic } from '@/core/lib/dnd-haptics';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

type ProfileModulesSectionProps = ComponentProps<typeof ProfileModulesSection>;

type ProfileEditModulesSectionProps = {
  modules: ProfileModuleDto[];
  onModulesChange: ProfileModulesSectionProps['onModulesChange'];
  isWorkspaceProfile: boolean;
  showManageTeamLink: boolean;
  readOnly: boolean;
  structureReadOnly: boolean;
  templateModules: ProfileModuleDto[];
  fieldLockedHintMessage: string;
  isModuleCustomized: (moduleId: string) => boolean;
  onResetModuleToTemplate: (moduleId: string) => void;
  overrideHintMessage: string;
  templateOverrideLabels: NonNullable<
    ProfileModulesSectionProps['templateOverrideLabels']
  >;
};

export function ProfileEditModulesSection({
  modules,
  onModulesChange,
  isWorkspaceProfile,
  showManageTeamLink,
  readOnly,
  structureReadOnly,
  templateModules,
  fieldLockedHintMessage,
  isModuleCustomized,
  onResetModuleToTemplate,
  overrideHintMessage,
  templateOverrideLabels,
}: ProfileEditModulesSectionProps) {
  return (
    <div className="flex min-w-0 flex-col gap-3 w-full">
      <ProfileModulesSection
        modules={modules}
        onModulesChange={onModulesChange}
        isWorkspaceProfile={isWorkspaceProfile}
        showManageTeamLink={showManageTeamLink}
        readOnly={readOnly}
        structureReadOnly={structureReadOnly}
        templateModules={templateModules}
        fieldLockedHintMessage={fieldLockedHintMessage}
        isModuleCustomized={isModuleCustomized}
        onResetModuleToTemplate={onResetModuleToTemplate}
        overrideHintMessage={overrideHintMessage}
        templateOverrideLabels={templateOverrideLabels}
      />
    </div>
  );
}

type ProfileEditPreviewColumnProps = {
  username?: string | null;
  workspaceId?: string | null;
  workspaceUsername?: string | null;
  previewProfileData: ProfileEditPreviewData;
};

export function ProfileEditPreviewColumn({
  username,
  workspaceId,
  workspaceUsername,
  previewProfileData,
}: ProfileEditPreviewColumnProps) {
  return (
    <Card
      data-slot="profile-preview-rail"
      className={cn(
        'flex min-w-0 flex-col',
        PROFILE_PREVIEW_RAIL_WIDTH_CLASS,
        stickyPhonePreviewRailClass(isCapacitorNative()),
      )}
    >
      <CardContent className="flex min-w-0 items-center justify-center overflow-visible px-6 py-6">
        <IPhonePreview
          username={username}
          workspaceId={workspaceId}
          workspaceUsername={workspaceUsername}
          profileData={previewProfileData}
          className="w-full"
        />
      </CardContent>
    </Card>
  );
}

type ProfileEditPreviewDialogProps = ProfileEditPreviewColumnProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ProfileEditPreviewDialog({
  open,
  onOpenChange,
  username,
  workspaceId,
  workspaceUsername,
  previewProfileData,
}: ProfileEditPreviewDialogProps) {
  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        void triggerTapHaptic();
        onOpenChange(next);
      }}
    >
      <ResponsiveDialogContent
        scrollable={false}
        className="max-w-4xl overflow-hidden border-b-0 p-0 md:max-h-[92vh] md:border-b flex flex-col"
      >
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden px-0 py-2 md:px-6 md:py-16">
          <IPhonePreview
            username={username}
            workspaceId={workspaceId}
            workspaceUsername={workspaceUsername}
            profileData={previewProfileData}
            className="w-full"
          />
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
